import { useState, useEffect } from "react";
import axios from "axios";
import { FiDownload, FiFilter, FiX, FiChevronDown } from "react-icons/fi";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function Sales() {
    const [stats, setStats] = useState({
        totalOrdersToday: 0,
        avgOrderValue: "0.00",
        totalRevenueToday: 0
    });
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDevice, setFilterDevice] = useState("");
    const [filterFlavor, setFilterFlavor] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");
                const [statsRes, ordersRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/orders/stats`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/orders`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);
                setStats(statsRes.data);
                setOrders(ordersRes.data);
            } catch (err) {
                console.error("Failed to fetch sales data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleExportCSV = () => {
        if (filteredOrders.length === 0) return alert("No data to export");

        const headers = ["Order ID", "Machine", "Flavours", "Base", "Size", "Status", "Amount (INR)", "Time"];
        const csvRows = filteredOrders.map(order => [
            order.order_id_PK,
            `"${order.device_name || 'N/A'}"`,
            `"${order.flavor_name || 'N/A'}"`,
            `"${order.base_name || 'N/A'}"`,
            `"${order.shake_size || 'N/A'}"`,
            order.status,
            order.total_price,
            `"${new Date(order.created_at).toLocaleString()}"`
        ]);

        const csvContent = [headers, ...csvRows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const fileName = `sales_report_${filterDevice || 'All_Machines'}_${filterFlavor || 'All_Flavours'}_${new Date().toISOString().split('T')[0]}.csv`.replace(/\s+/g, '_');

        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const uniqueDevices = [...new Set(orders.map(o => o.device_name))].filter(Boolean);
    const uniqueFlavors = [...new Set(orders.map(o => o.flavor_name))].filter(Boolean);

    const filteredOrders = orders.filter(order => {
        const matchDevice = filterDevice === "" || order.device_name === filterDevice;
        const matchFlavor = filterFlavor === "" || order.flavor_name === filterFlavor;
        return matchDevice && matchFlavor;
    });

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center font-bold">Loading Sales Data...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 ">

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-400 text-sm">Total Orders Today</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-2">{stats.totalOrdersToday}</h2>
                            <p className="text-green-500 text-xs mt-1">Live from machine fleet</p>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-400 text-sm">Average Order Value</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-2">₹{stats.avgOrderValue}</h2>
                            <p className="text-gray-500 text-xs mt-1">Calculated today</p>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-gray-400 text-sm">Total Revenue</p>
                            <h2 className="text-green-500 text-3xl font-bold mt-2">₹{stats.totalRevenueToday}</h2>
                            <p className="text-green-500 text-xs mt-1">Daily aggregated revenue</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 mt-8 ">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg font-primary">
                    <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
                        <div>
                            <h2 className="text-xl font-semibold text-green-400">Transaction History</h2>
                            <p className="text-xs text-gray-500 mt-1">Displaying {filteredOrders.length} records</p>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap bg-black/40 p-1.5 rounded-xl border border-white/5 shadow-inner">
                            <div className="flex items-center gap-2 px-3 text-gray-500 border-r border-white/5 mr-1">
                                <FiFilter size={14} className="text-green-500/50" />
                                <span className="text-[10px] uppercase font-black tracking-widest">Filter By</span>
                            </div>

                            <div className="relative">
                                <select
                                    value={filterDevice}
                                    onChange={(e) => setFilterDevice(e.target.value)}
                                    className="bg-[#151515] border border-gray-800 text-gray-300 text-[11px] font-bold rounded-lg px-4 py-2 outline-none focus:ring-1 focus:ring-green-400/30 transition cursor-pointer hover:bg-[#1e1e1e] hover:border-gray-700 min-w-[150px] appearance-none"
                                >
                                    <option value="">All Machines</option>
                                    {uniqueDevices.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
                                    <FiChevronDown size={12} />
                                </div>
                            </div>

                            <div className="relative">
                                <select
                                    value={filterFlavor}
                                    onChange={(e) => setFilterFlavor(e.target.value)}
                                    className="bg-[#151515] border border-gray-800 text-gray-300 text-[11px] font-bold rounded-lg px-4 py-2 outline-none focus:ring-1 focus:ring-green-400/30 transition cursor-pointer hover:bg-[#1e1e1e] hover:border-gray-700 min-w-[150px] appearance-none"
                                >
                                    <option value="">All Flavours</option>
                                    {uniqueFlavors.map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600">
                                    <FiChevronDown size={12} />
                                </div>
                            </div>

                            {(filterDevice || filterFlavor) && (
                                <button
                                    onClick={() => { setFilterDevice(""); setFilterFlavor(""); }}
                                    className="p-2 text-gray-500 hover:text-red-400 transition bg-black/40 rounded-lg hover:bg-red-400/5 ml-1"
                                    title="Clear Filters"
                                >
                                    <FiX size={16} />
                                </button>
                            )}

                            <div className="h-6 w-[1px] bg-white/5 mx-2" />

                            <button
                                onClick={handleExportCSV}
                                className="flex items-center gap-2 px-6 py-2 text-[11px] font-black uppercase tracking-widest border border-green-500/40 rounded-lg text-green-400 hover:bg-green-500 hover:text-black transition shadow-[0_0_15px_rgba(74,222,128,0.1)] active:scale-95"
                            >
                                <FiDownload size={14} />
                                Export
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300 ">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-400">
                                    <th className="px-4 py-3 text-left">Order ID</th>
                                    <th className="px-4 py-3 text-left">Machine</th>
                                    <th className="px-4 py-3 text-left">Flavours</th>
                                    <th className="px-4 py-3 text-left">Base</th>
                                    <th className="px-4 py-3 text-left">Size</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Amount</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredOrders.map((order) => (
                                    <tr
                                        key={order.order_id_PK}
                                        className="border-b border-gray-800 hover:bg-[#1e1e1e] transition"
                                    >
                                        <td className="px-4 py-3 font-medium text-white">
                                            {order.order_id_PK}
                                        </td>

                                        <td className="px-4 py-3 text-gray-400">
                                            {order.device_name}
                                        </td>

                                        <td className="px-4 py-3 text-white">
                                            {order.flavor_name}
                                        </td>

                                        <td className="px-4 py-3 text-white">{order.base_name}</td>
                                        <td className="px-4 py-3 text-white">{order.shake_size}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${order.status === 'paid' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                                                {order.status}
                                            </span>
                                        </td>

                                        <td className="px-4 py-3 text-green-400 font-semibold">
                                            ₹{order.total_price}
                                        </td>

                                        <td className="px-4 py-3 text-gray-500">
                                            {new Date(order.created_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                                {filteredOrders.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500 italic">No orders found matching filters</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sales;
